/**
 * What the left map-tools column (zoom, home, previous / next extent, find my location, the compass) needs of the
 * view on screen. Map and Globe give it their engine; BIM gives it the model camera, through the seam below, so the
 * column works the same in all three views and never imports the BIM view.
 *
 * The BIM view registers its camera with `useBimViewCamera.getState().register(...)` while its model is up
 * (features/bim/seams.ts) and `register(null)` when it goes; until then the column's buttons are disabled in BIM.
 */
import type { CameraState, CameraTarget, MapEngine, Unsubscribe } from '@mighty-map/map-engine';
import { create } from 'zustand';

export interface ViewCamera {
  /** Where the view is, in the map's terms; null before it has a camera. */
  getCamera(): CameraState | null;
  /** Move at once; the parts of the target left out stay as they are. */
  jumpTo(target: CameraTarget): void;
  /** The theme's starting view (Home). A view with a home of its own goes there instead. */
  goHome(home: CameraTarget): void;
  /** Called when the camera settles after a move. */
  onCameraChange(handler: () => void): Unsubscribe;
  /** Mark where the viewer is (Find my location). */
  markLocation(lon: number, lat: number): void;
}

/** The overlay Find my location draws on the map. */
export const LOCATION_OVERLAY = 'mm-locate';

/** Map and Globe: the engine already speaks the map's camera. */
export function engineCamera(engine: MapEngine): ViewCamera {
  return {
    getCamera: () => engine.getCamera(),
    jumpTo: (target) => engine.jumpTo(target),
    goHome: (home) => engine.jumpTo({ bearing: 0, pitch: 0, ...home }),
    onCameraChange: (handler) => engine.onCameraChange(() => handler()),
    markLocation: (lon, lat) =>
      engine.draw.setOverlay(LOCATION_OVERLAY, [{ geometry: { type: 'Point', coordinates: [lon, lat] } }], 'highlight'),
  };
}

interface BimViewCameraState {
  camera: ViewCamera | null;
  register: (camera: ViewCamera | null) => void;
}

/** THE BIM SEAM for the left map-tools column: the model camera while the BIM view's model is up. */
export const useBimViewCamera = create<BimViewCameraState>()((set) => ({
  camera: null,
  register: (camera) => set({ camera }),
}));
